import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ErrorType, SoftError } from 'types/error';
import { v4 as uuidv4 } from 'uuid';

import ERRORS from '~/constants/errors';

import { errorSchema } from '../services/policy';
import { hasProperty } from '../utils/types';

export interface Decline {
  errors: ErrorType[];
  softErrors: SoftError[];
  hardErrors: ErrorType[];
}

const initialState: Decline = {
  errors: [],
  softErrors: [],
  hardErrors: [],
};

const handleDecline = (payload: ErrorType | ErrorType[] | SoftError | SoftError[]) => {
  if (Array.isArray(payload)) {
    return payload.map((errorDetails) => ({
      ...errorDetails,
      code: errorDetails.code || uuidv4(),
    }));
  }

  return [
    {
      ...payload,
      code: payload.code || uuidv4(),
    },
  ];
};

export const declineSlice = createSlice({
  name: 'decline',
  initialState,
  reducers: {
    decline: (state, action: PayloadAction<ErrorType | ErrorType[]>) => {
      state.errors = handleDecline(action.payload) as ErrorType[];
    },
    genericDecline: (state, action: PayloadAction<ErrorType | string | undefined>) => {
      if (hasProperty(action.payload, 'data')) {
        const error = errorSchema.safeParse(action.payload.data);
        // Filter out payment errors as it is handled in the payment code. This logic need to be refactored.
        if (error.success && error.data.errors[0].code.startsWith('PAYMENT_ERROR')) return;
      }
      const error =
        typeof action.payload === 'string' ? { message: action.payload } : action.payload;
      state.errors = [{ code: uuidv4(), ...ERRORS.genericDecline, ...error }];
    },
    softDecline: (state, action: PayloadAction<SoftError | SoftError[]>) => {
      state.softErrors = handleDecline(action.payload) as SoftError[];
    },
    hardDecline: (state, action: PayloadAction<ErrorType | ErrorType[]>) => {
      state.hardErrors = handleDecline(action.payload) as ErrorType[];
    },
    clearSoftError: (state) => {
      state.softErrors = [];
    },
  },
});

export const { decline, softDecline, hardDecline, clearSoftError, genericDecline } =
  declineSlice.actions;

export default declineSlice.reducer;
